// src/components/SaveButton.js - Simple manual save button

import React, { useState } from 'react';
import {
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box
} from '@mui/material';
import {
  Save as SaveIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { useFormContext } from '../context/FormContext';

const SaveButton = () => {
  const { state, actions } = useFormContext();
  const [message, setMessage] = useState({ open: false, type: '', text: '' });
  const [restoreDialog, setRestoreDialog] = useState(false);
  const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);

  const handleSaveClick = () => {
    // Always show confirmation dialog for save
    setSaveConfirmDialog(true);
  };

  const handleSave = async () => {
    try {
      const success = await actions.saveProgress();
      if (success) {
        setMessage({ open: true, type: 'success', text: 'Progress saved successfully!' });
      } else {
        setMessage({ open: true, type: 'error', text: 'Failed to save progress' });
      }
      setSaveConfirmDialog(false);
    } catch (error) {
      setMessage({ open: true, type: 'error', text: 'Failed to save progress' });
      setSaveConfirmDialog(false);
    }
  };

  const handleRestore = async () => {
    try {
      const success = await actions.loadProgress();
      if (success) {
        setMessage({ open: true, type: 'success', text: 'Progress restored successfully!' });
        setRestoreDialog(false);
      } else {
        setMessage({ open: true, type: 'warning', text: 'No saved progress found' });
      }
    } catch (error) {
      console.error('❌ Restore error:', error);
      setMessage({ open: true, type: 'error', text: 'Failed to restore progress' });
    }
  };

  const handleCloseMessage = () => {
    setMessage({ ...message, open: false });
  };

  const savedInfo = actions.getSavedInfo();

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Save Progress">
          <IconButton 
            onClick={handleSaveClick} 
            color="inherit" 
            size="small"
          >
            <SaveIcon />
          </IconButton>
        </Tooltip>

        {savedInfo && (
          <Tooltip title="Restore Saved Progress">
            <IconButton 
              onClick={() => setRestoreDialog(true)} 
              color="inherit" 
              size="small"
            >
              <RestoreIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Restore Dialog */}
      <Dialog open={restoreDialog} onClose={() => setRestoreDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Restore Saved Progress</DialogTitle>
        <DialogContent>
          {savedInfo ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Last Saved:</strong> {new Date(savedInfo.timestamp).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Report Type:</strong> {savedInfo.reportType}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Has Patient Data:</strong> {savedInfo.hasPatientData ? 'Yes' : 'No'}
              </Typography>
              <Alert severity="warning">
                This will restore all saved form data. Any unsaved changes will be lost.
              </Alert>
            </Box>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No saved progress found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreDialog(false)}>Cancel</Button>
          {savedInfo && (
            <Button onClick={handleRestore} variant="contained" color="primary">
              Restore Progress
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Save Confirmation Dialog */}
      <Dialog open={saveConfirmDialog} onClose={() => setSaveConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Save</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to save your current progress? This will overwrite any previously saved data.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveConfirmDialog(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Yes, Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={message.open}
        autoHideDuration={3000}
        onClose={handleCloseMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseMessage} severity={message.type} sx={{ width: '100%' }}>
          {message.text}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SaveButton;